import 'regenerator-runtime/runtime';
import { call, select, takeEvery } from 'redux-saga/effects';
import {
  DoRequestResponse,
  getEndpointUrl,
  GlobalAction,
  GlobalActions,
  GlobalPut,
} from '@ps20-frontend/shop-integration-redux';
import {
  Adresse,
  CrmRequest,
  CrmSucheImmobilienResponse,
  GebaeudeAmis,
  Role,
} from '@ps20/bff-models';
import { App, Immobilie, Risikoort } from '@ps20-frontend/frontend-models';
import get from 'lodash/get';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { ForkEffect } from '@redux-saga/core/effects';
import { IAppState } from '../localStore';
import { shouldUseSdwEndpoint } from '../selectors/transientState.selector';
import { checkActionType } from './helpers/checkActionType';

export function* watchAmisCrmImmobilieRequested(): Generator<ForkEffect> {
  yield takeEvery(
    GlobalActions.AMIS_CRM_IMMOBILIE_REQUESTED,
    safeSaga(amisCrmImmobilieRequested, App.SHOP),
  );
  yield takeEvery(
    GlobalActions.AMIS_CRM_IMMOBILIE_REQUEST_SUCCESSFUL,
    safeSaga(successCallback, App.SHOP),
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function* amisCrmImmobilieRequested(action: GlobalAction<unknown>): any {
  checkActionType(action.type, GlobalActions.AMIS_CRM_IMMOBILIE_REQUESTED);
  const state: IAppState = yield select();

  const pid = state.transientState.datenhaltung.kdv.perId;
  const role = state.transientState.role;

  if (pid && role !== Role.SPS) {
    const body: CrmRequest = {
      personId: pid,
    };
    const globalPut = GlobalPut.getInstance();
    yield call(
      globalPut.put.bind(globalPut),
      new GlobalActions().doRequest({
        appId: App.SHOP,
        body,
        url: getEndpointUrl(shouldUseSdwEndpoint(state), '/privatschutz/api/amiscrmsucheImmobilien'),
        successEvent: GlobalActions.AMIS_CRM_IMMOBILIE_REQUEST_SUCCESSFUL,
        errorEvent: GlobalActions.AMIS_CRM_IMMOBILIE_REQUEST_FAILED,
        noCache: true,
      }),
    );
  }
}
export function* successCallback(
  successAction: DoRequestResponse<CrmSucheImmobilienResponse>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any {
  const globalPut = GlobalPut.getInstance();
  const transformedImmobilien = transformAmisImmobilien(successAction.payload);
  yield call(
    [globalPut, globalPut.put],
    new GlobalActions().amisImmobilienTransformed(transformedImmobilien),
  );
}

export function transformBaujahr(baujahrFromAmis: number | null): number {
  if (baujahrFromAmis === null || baujahrFromAmis < 0) {
    return null;
  }

  return baujahrFromAmis;
}

export const transformAmisImmobilien = function (
  amisCrmImmobilieResponse: CrmSucheImmobilienResponse,
): Array<Immobilie> {
  const amisImmobilien: GebaeudeAmis[] = amisCrmImmobilieResponse.immobilien;
  let transformedImmobilien: Array<Immobilie> = [];

  if (amisImmobilien && amisImmobilien.length > 0) {
    transformedImmobilien = amisImmobilien.map(immobilie => {
      return transformGebauede(immobilie);
    });
  }
  return transformedImmobilien;
};

export const transformGebauede = function (gebaeudeFromAmis: GebaeudeAmis): Immobilie {
  const nebengebaeudeWohnflaeche = get(gebaeudeFromAmis, 'nebengebaeudeQM', null);
  return {
    id: gebaeudeFromAmis.kdvUuid,
    risikoort: adresseToRisikoort(gebaeudeFromAmis.adresse),
    wohnflaeche: get(gebaeudeFromAmis, 'wohnflaecheQM', null),
    baujahr: transformBaujahr(gebaeudeFromAmis.baujahr),
    ausgewaehlt: false,
    kellerVorhanden: get(gebaeudeFromAmis, 'kellerVorhanden', null),
    kellerMitWohnflaeche: get(gebaeudeFromAmis, 'kellerMitWohnflaeche', null),
    flachdach: get(gebaeudeFromAmis, 'flachdach', null),
    ferienhaus: get(gebaeudeFromAmis, 'ferienhaus', null),
    nebengebaeudeQM: nebengebaeudeWohnflaeche,
    nebengebaeudeVorhanden: nebengebaeudeWohnflaeche > 0 ? true : null,
    oeltank: get(gebaeudeFromAmis, 'oeltank', null),
    gebaeudeArtKdv: get(gebaeudeFromAmis, 'gebaeudeArtKdv', null),
    stockwerk: get(gebaeudeFromAmis, 'stockwerk', null),
    wohnungsnr: get(gebaeudeFromAmis, 'whgNummer', null),
  };
};

export function adresseToRisikoort(adresse: Adresse): Risikoort {
  return {
    ort: get(adresse, 'ort', null),
    strasse: get(adresse, 'strasse', null),
    hausnrzus: get(adresse, 'hausnrzus', null),
    hausnummer: get(adresse, 'hausnummer', null),
    land: get(adresse, 'land', null),
    plz: get(adresse, 'plz', null),
    ortsteil: get(adresse, 'ortsteil', null),
    status: adresse.validierungsStatus,
  };
}
